import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { from, Observable, throwError, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import {
  FreedomPayRequest,
  FreedomPayRequestItem,
  FreedomPayResponse,
  FreedomPayTerminalConfig,
  NormalizedTicket,
  PosPaymentRequest,
  RefundResponse,
  Ticket,
  TicketItem
} from 'src/app/models';
import * as uuid from 'uuid';
import { APP_STATE_TOKEN } from '../../store/app/app.state.model';
import { BaseService } from '../base.service';
import { ErrorService } from '../error/error.service';
import { HttpPluginService } from '../http-plugin/http-plugin.service';
import { SqliteService } from '../sqlite/sqlite.service';

@Injectable({
  providedIn: 'root'
})
export class FreedomPayService extends BaseService {
  constructor(
    private http: HttpClient,
    store: Store,
    private httpPluginService: HttpPluginService,
    private readonly errorService: ErrorService,
    private sqliteService: SqliteService
  ) {
    super(store);
  }

  private getTerminalConfig(terminal_id: number): FreedomPayTerminalConfig {
    const terminal = this.store
      .selectSnapshot(APP_STATE_TOKEN)
      .facility_details.card_terminals.find((t) => t.id === terminal_id);

    if (!terminal) {
      throw new Error(
        `Terminal with ID ${terminal_id} not found in facility configuration`
      );
    }

    return {
      terminal_ip: terminal.ip,
      terminal_port: (terminal as any).port || 7000, // Default FreedomPay port
      store_id: (terminal as any).freedompay_store_id || 'DEFAULT_STORE',
      terminal_id: (terminal as any).freedompay_terminal_id,
      merchant_id: (terminal as any).freedompay_merchant_id,
      token_type: '6',
      floor_limit: '100.00',
      allow_partial: 'Y',
      selling_system_name: 'MEALSUITE',
      selling_system_version: '1.0',
      selling_system_middleware_name: 'MealSuite API',
      selling_system_middleware_version: '1.0',
      client_environment: 'MealSuite POS 1.0'
    };
  }

  private buildXmlItem(item: FreedomPayRequestItem, index: number): string {
    const xmlnsValue = 'http://freeway.freedompay.com/';
    const totalAmount = item.totalAmount;
    const itemTaxAmount = item.taxAmount;
    //product name to 15 chars remove special chars
    const productName = item.productName.replace(/[^a-zA-Z0-9 ]/g, '');
    const productDescription = item.productDescription.replace(
      /[^a-zA-Z0-9 ]/g,
      ''
    );
    return `        <Item id="${index + 1}">
            <taxIncludedFlag xmlns="${xmlnsValue}">${
      item.taxIncludedFlag
    }</taxIncludedFlag>
            <productCode xmlns="${xmlnsValue}">${item.productCode}</productCode>
            <productUPC xmlns="${xmlnsValue}">${item.productUPC}</productUPC>
            <productSKU xmlns="${xmlnsValue}">${item.productSKU}</productSKU>
            <productName xmlns="${xmlnsValue}">${productName}</productName>
            <productDescription xmlns="${xmlnsValue}">${productDescription}</productDescription>
            <unitPrice xmlns="${xmlnsValue}">${item.unitPrice}</unitPrice>
            <quantity xmlns="${xmlnsValue}">1</quantity>
            <totalAmount xmlns="${xmlnsValue}">${totalAmount}</totalAmount>
            <taxAmount xmlns="${xmlnsValue}">${itemTaxAmount}</taxAmount>
            <saleCode xmlns="${xmlnsValue}">${item.saleCode}</saleCode>
        </Item>`;
  }

  private buildRefundXmlRequest(freedomPayRequest: FreedomPayRequest): string {
    return `<?xml version="1.0" encoding="UTF-8"?>
<POSRequest>
    <RequestType>Refund</RequestType>
    <ChargeAmount>${freedomPayRequest.charge_amount}</ChargeAmount>
    <ClientEnvironment>${freedomPayRequest.client_environment}</ClientEnvironment>
    <StoreId>${freedomPayRequest.store_id}</StoreId>
    <TerminalId>${freedomPayRequest.terminal_id}</TerminalId>
    <MerchantReferenceCode>${freedomPayRequest.merchant_reference_code}</MerchantReferenceCode>
    <InvoiceNumber>${freedomPayRequest.invoice_number}</InvoiceNumber>
    <SellingSystem>${freedomPayRequest.selling_system_name}</SellingSystem>
    <SellingSystemVersion>${freedomPayRequest.selling_system_version}</SellingSystemVersion>
    <RequestGuid>${freedomPayRequest.request_guid}</RequestGuid>
    <RequestId>${freedomPayRequest.request_id}</RequestId>
</POSRequest>`;
  }

  private buildVoidXmlRequest(freedomPayRequest: FreedomPayRequest): string {
    return `<?xml version="1.0" encoding="UTF-8"?>
<POSRequest>
 <RequestType>Void</RequestType>
 <ChargeAmount>${freedomPayRequest.charge_amount}</ChargeAmount>
 <ClientEnvironment>${freedomPayRequest.client_environment}</ClientEnvironment>
 <StoreId>${freedomPayRequest.store_id}</StoreId>
 <TerminalId>${freedomPayRequest.terminal_id}</TerminalId>
 <MerchantReferenceCode>${freedomPayRequest.merchant_reference_code}</MerchantReferenceCode>
 <InvoiceNumber>${freedomPayRequest.invoice_number}</InvoiceNumber>
 <SellingSystem>${freedomPayRequest.selling_system_name}</SellingSystem>
 <SellingSystemVersion>${freedomPayRequest.selling_system_version}</SellingSystemVersion>
 <RequestGuid>${freedomPayRequest.request_guid}</RequestGuid>
 <RequestId>${freedomPayRequest.request_id}</RequestId>
</POSRequest>`;
  }

  private buildXmlQueryDeviceStatus(): string {
    return `<?xml version="1.0" encoding="UTF-8"?>
<POSRequest>
    <RequestType>SupportDeviceInfo</RequestType>
</POSRequest>`;
  }

  private buildQueryOfflineReplayStatusRequest(requestId: string): string {
    return `<?xml version="1.0" encoding="UTF-8"?>
<POSRequest>
    <RequestType>GetReplayStatus</RequestType>
    <RequestId>${requestId}</RequestId>
</POSRequest>`;
  }

  private buildXmlRequest(freedomPayRequest: FreedomPayRequest): string {
    const chargeAmount = freedomPayRequest.charge_amount;
    const itemsXml = freedomPayRequest.items
      .map((item, index) => this.buildXmlItem(item, index))
      .join('\n');

    const taxAmount = freedomPayRequest.tax_amount;
    return `<?xml version="1.0" encoding="UTF-8"?>
<POSRequest>
    <RequestType>Sale</RequestType>
    <ClientEnvironment>${freedomPayRequest.client_environment}</ClientEnvironment>
    <StoreId>${freedomPayRequest.store_id}</StoreId>
    <TerminalId>${freedomPayRequest.terminal_id}</TerminalId>
    <TokenType>${freedomPayRequest.token_type}</TokenType>
    <FloorLimit>${freedomPayRequest.floor_limit}</FloorLimit>
    <AllowPartial>${freedomPayRequest.allow_partial}</AllowPartial>
    <MerchantReferenceCode>${freedomPayRequest.merchant_reference_code}</MerchantReferenceCode>
    <InvoiceNumber>${freedomPayRequest.invoice_number}</InvoiceNumber>
    <SellingSystemName>${freedomPayRequest.selling_system_name}</SellingSystemName>
    <SellingSystemVersion>${freedomPayRequest.selling_system_version}</SellingSystemVersion>
    <SellingSystemMiddlewareName>${freedomPayRequest.selling_system_middleware_name}</SellingSystemMiddlewareName>
    <SellingSystemMiddlewareVersion>${freedomPayRequest.selling_system_middleware_version}</SellingSystemMiddlewareVersion>
    <Items>
${itemsXml}
    </Items>
    <ChargeAmount>${chargeAmount}</ChargeAmount>
    <TaxAmount>${taxAmount}</TaxAmount>
</POSRequest>`;
  }

  private parseXmlResponse(xmlResponse: string): FreedomPayResponse {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlResponse, 'text/xml');

    // Check for parsing errors
    const parseError = xmlDoc.querySelector('parsererror');
    if (parseError) {
      throw new Error('Failed to parse FreedomPay response XML');
    }

    const response: FreedomPayResponse = {};
    const root = xmlDoc.documentElement;

    if (root.tagName !== 'POSResponse') {
      throw new Error('Invalid FreedomPay response format');
    }

    // Map XML elements to response object
    const elementMappings = [
      'ApprovalCode',
      'ApprovedAmount',
      'BatteryLevel',
      'Brand',
      'CardType',
      'ChipData',
      'DCCAccepted',
      'Decision',
      'DeviceVerified',
      'EmvTagData',
      'EntryMode',
      'ErrorCode',
      'ExpiryDate',
      'IssuerName',
      'MaskedCardNumber',
      'MerchantReferenceCode',
      'Message',
      'Network',
      'PinVerified',
      'ProcessorResponseCode',
      'ProcessorResponseMessage',
      'ReceiptText',
      'RequestGuid',
      'RequestId',
      'SignatureRequired',
      'TipAmount',
      'TransactionId'
    ];

    elementMappings.forEach((elementName) => {
      const element = root.querySelector(elementName);
      if (element && element.textContent) {
        response[elementName] = element.textContent;
      }
    });

    return response;
  }

  private flattenTicketItems(
    items: TicketItem[],
    parentItemName?: string
  ): FreedomPayRequestItem[] {
    const flattenedItems: FreedomPayRequestItem[] = [];

    for (const item of items) {
      // Add the current item
      const productDescription = parentItemName
        ? `Modifier of ${parentItemName}`
        : item.name || 'Item';

      flattenedItems.push({
        productCode: 'MEALSUProduct',
        productUPC:
          item.pos_product_id?.toString() ||
          item.food_id?.toString() ||
          '123456789',
        productSKU:
          item.pos_product_id?.toString() ||
          item.food_id?.toString() ||
          '123456789',
        productName: item.name || 'Item',
        productDescription,
        unitPrice: item.base_price?.toString() || '0.00',
        quantity: '1',
        totalAmount: item.total?.toString() || '0.00',
        taxAmount: item.tax_amount
          ? Number(item.tax_amount).toString()
          : '0.00',
        saleCode: 'S',
        taxIncludedFlag: 'N'
      });

      // Recursively add nested items (modifiers)
      if (item.ticket_items && item.ticket_items.length > 0) {
        const nestedItems = this.flattenTicketItems(
          item.ticket_items,
          item.name
        );
        flattenedItems.push(...nestedItems);
      }
    }

    return flattenedItems;
  }

  private buildFreedomSalePayRequest(
    ticket: Ticket,
    paymentRequest: PosPaymentRequest
  ): FreedomPayRequest {
    const config = this.getTerminalConfig(paymentRequest.payment_terminal_id);
    const requestGuid = uuid.v4();

    // Convert ticket items to FreedomPay items (including nested modifiers)
    const items: FreedomPayRequestItem[] = this.flattenTicketItems(
      ticket.ticket_items || []
    );

    return {
      request_type: 'Sale',
      charge_amount: paymentRequest.amount.toString(),
      tax_amount: ticket.tax_total?.toString() || '0.00',
      client_environment: config.client_environment,
      store_id: config.store_id,
      terminal_id: config.terminal_id,
      token_type: config.token_type,
      floor_limit: config.floor_limit,
      allow_partial: config.allow_partial,
      merchant_reference_code: config.merchant_id,
      invoice_number: ticket.id.toString(),
      selling_system_name: config.selling_system_name,
      selling_system_version: config.selling_system_version,
      selling_system_middleware_name: config.selling_system_middleware_name,
      selling_system_middleware_version:
        config.selling_system_middleware_version,
      request_guid: requestGuid,
      items
    };
  }

  private buildFreedomVoidPayRequest(
    ticket: Ticket | NormalizedTicket,
    response: FreedomPayResponse,
    config: FreedomPayTerminalConfig
  ): FreedomPayRequest {
    return {
      request_type: 'Void',
      charge_amount: response.ApprovedAmount.toString(),
      client_environment: config.client_environment,
      store_id: config.store_id,
      terminal_id: config.terminal_id,
      merchant_reference_code: response.MerchantReferenceCode,
      invoice_number: ticket.id.toString(),
      selling_system_name: config.selling_system_name,
      selling_system_version: config.selling_system_version,
      request_guid: response.RequestGuid,
      request_id: response.RequestId
    };
  }

  private buildFreedomRefundRequest(
    terminal_id: number,
    merchant_reference_code: string,
    charge_amount: number,
    original_request_id: string,
    original_request_guid: string,
    invoice_number: number
  ): FreedomPayRequest {
    const config = this.getTerminalConfig(terminal_id);

    return {
      request_type: 'Refund',
      charge_amount: Math.abs(charge_amount).toString(),
      client_environment: config.client_environment,
      store_id: config.store_id,
      terminal_id: config.terminal_id,
      merchant_reference_code,
      invoice_number: invoice_number.toString(),
      selling_system_name: config.selling_system_name,
      selling_system_version: config.selling_system_version,
      request_guid: original_request_guid,
      request_id: original_request_id
    };
  }

  private buildFreedomCancelPayRequest(
    ticket: Ticket,
    paymentRequest: PosPaymentRequest
  ): FreedomPayRequest {
    const config = this.getTerminalConfig(paymentRequest.payment_terminal_id);
    const requestGuid = uuid.v4();

    // Convert ticket items to FreedomPay items (including nested modifiers)
    const items: FreedomPayRequestItem[] = this.flattenTicketItems(
      ticket.ticket_items || []
    );

    return {
      request_type: 'Cancel',
      charge_amount: paymentRequest.amount.toString(),
      tax_amount: ticket.tax_total?.toString() || '0.00',
      client_environment: config.client_environment,
      store_id: config.store_id,
      terminal_id: config.terminal_id,
      merchant_reference_code: config.merchant_id,
      invoice_number: ticket.id.toString(),
      selling_system_name: config.selling_system_name,
      selling_system_version: config.selling_system_version,
      request_guid: requestGuid,
      items
    };
  }

  processPayment(
    ticket: Ticket,
    paymentRequest: PosPaymentRequest
  ): Observable<FreedomPayResponse> {
    const config = this.getTerminalConfig(paymentRequest.payment_terminal_id);
    const freedomPayRequest = this.buildFreedomSalePayRequest(
      ticket,
      paymentRequest
    );
    const xmlRequest = this.buildXmlRequest(freedomPayRequest);
    const url = `https://${config.terminal_ip}:${config.terminal_port}/`;
    const headers = { 'Content-Type': 'application/xml' };

    return from(this.httpPluginService.postXml(url, xmlRequest, headers)).pipe(
      switchMap((resp) => {
        if (!resp) {
          return throwError(
            () => new Error('Native HTTP client not available (Mobile only)')
          );
        }

        if (resp.status >= 200 && resp.status < 300) {
          const parsedResponse = this.parseXmlResponse(resp.data);
          return [parsedResponse];
        } else {
          const errorMessage = `Terminal communication failed: HTTP ${
            resp.status
          } - ${resp.statusText || 'Unknown error'}`;
          return throwError(() => new Error(errorMessage));
        }
      }),
      catchError((error: any) => {
        let errorMessage = 'Terminal communication failed';

        if (error.message?.includes('timeout')) {
          errorMessage += `: Failed due to timeout. Please check the terminal configuration.`;
        } else if (error.message?.includes('network')) {
          errorMessage += `: Network error occurred. Please check network connectivity.`;
        } else {
          errorMessage += `: ${error.message || 'Unknown error'}`;
        }
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  voidPayment(
    ticket: Ticket | NormalizedTicket,
    saleResponse: FreedomPayResponse,
    terminal_id: number
  ): Observable<FreedomPayResponse> {
    const config = this.getTerminalConfig(terminal_id);
    const voidRequest = this.buildFreedomVoidPayRequest(
      ticket,
      saleResponse,
      config
    );
    const xmlRequest = this.buildVoidXmlRequest(voidRequest);
    const url = `https://${config.terminal_ip}:${config.terminal_port}/`;
    const headers = { 'Content-Type': 'application/xml' };

    return from(this.httpPluginService.postXml(url, xmlRequest, headers)).pipe(
      switchMap((resp) => {
        if (!resp) {
          return throwError(
            () => new Error('Native HTTP client not available (Mobile only)')
          );
        }

        if (resp.status >= 200 && resp.status < 300) {
          const parsedResponse = this.parseXmlResponse(resp.data);
          return [parsedResponse];
        } else {
          const errorMessage = `Void request failed: HTTP ${resp.status} - ${
            resp.statusText || 'Unknown error'
          }`;
          return throwError(() => new Error(errorMessage));
        }
      }),
      catchError((error: any) => {
        const errorMessage = `Void request failed: ${
          error.message || 'Unknown error'
        }`;
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  voidRefund(
    ticket: Ticket | NormalizedTicket,
    refundResponse: FreedomPayResponse,
    terminal_id: number
  ): Observable<FreedomPayResponse> {
    const config = this.getTerminalConfig(terminal_id);
    const voidRequest = this.buildFreedomVoidPayRequest(
      ticket,
      refundResponse,
      config
    );
    const xmlRequest = this.buildVoidXmlRequest(voidRequest);
    const url = `https://${config.terminal_ip}:${config.terminal_port}/`;
    const headers = { 'Content-Type': 'application/xml' };
    return from(this.httpPluginService.postXml(url, xmlRequest, headers)).pipe(
      switchMap((resp) => {
        if (!resp) {
          return throwError(
            () => new Error('Native HTTP client not available (Mobile only)')
          );
        }

        if (resp.status >= 200 && resp.status < 300) {
          const parsedResponse = this.parseXmlResponse(resp.data);
          return [parsedResponse];
        } else {
          const errorMessage = `Void refund request failed: HTTP ${
            resp.status
          } - ${resp.statusText || 'Unknown error'}`;
          return throwError(() => new Error(errorMessage));
        }
      }),
      catchError((error: any) => {
        const errorMessage = `Void refund request failed: ${
          error.message || 'Unknown error'
        }`;
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  processRefund(
    refundData: RefundResponse,
    selectedTerminalId: number
  ): Observable<FreedomPayResponse> {
    const config = this.getTerminalConfig(selectedTerminalId);
    const refundRequest = this.buildFreedomRefundRequest(
      selectedTerminalId,
      refundData.merchant_reference_code,
      refundData.remaining_refund_due,
      refundData.request_id,
      refundData.request_guid,
      refundData.original_ticket_id
    );
    const xmlRequest = this.buildRefundXmlRequest(refundRequest);
    const url = `https://${config.terminal_ip}:${config.terminal_port}/`;
    const headers = { 'Content-Type': 'application/xml' };

    return from(this.httpPluginService.postXml(url, xmlRequest, headers)).pipe(
      switchMap((resp) => {
        if (!resp) {
          return throwError(
            () => new Error('Native HTTP client not available (Mobile only)')
          );
        }

        if (resp.status >= 200 && resp.status < 300) {
          const parsedResponse = this.parseXmlResponse(resp.data);
          return [parsedResponse];
        } else {
          const errorMessage = `Refund request failed: HTTP ${resp.status} - ${
            resp.statusText || 'Unknown error'
          }`;
          return throwError(() => new Error(errorMessage));
        }
      }),
      catchError((error: any) => {
        let errorMessage = 'Terminal refund failed';

        if (
          error.message?.includes('timeout') ||
          error.message?.includes('network')
        ) {
          errorMessage += `: Cannot connect to terminal at ${config.terminal_ip}:${config.terminal_port}. Please check network connectivity and terminal configuration.`;
        } else {
          errorMessage += `: ${error.message || 'Unknown error'}`;
        }

        return throwError(() => new Error(errorMessage));
      })
    );
  }

  processReplayStatus(
    request_id: string,
    terminal_id: number
  ): Observable<FreedomPayResponse> {
    const config = this.getTerminalConfig(terminal_id);
    const xmlRequest = this.buildQueryOfflineReplayStatusRequest(request_id);
    const url = `https://${config.terminal_ip}:${config.terminal_port}/`;
    const headers = { 'Content-Type': 'application/xml' };

    return from(this.httpPluginService.postXml(url, xmlRequest, headers)).pipe(
      switchMap((resp) => {
        if (!resp) {
          return throwError(
            () => new Error('Native HTTP client not available (Mobile only)')
          );
        }

        if (resp.status >= 200 && resp.status < 300) {
          const parsedResponse = this.parseXmlResponse(resp.data);
          return [parsedResponse];
        } else {
          const errorMessage = `Replay status query failed: HTTP ${
            resp.status
          } - ${resp.statusText || 'Unknown error'}`;
          return throwError(() => new Error(errorMessage));
        }
      }),
      catchError((error: any) => {
        let errorMessage = 'Replay status query failed';

        if (
          error.message?.includes('timeout') ||
          error.message?.includes('network')
        ) {
          errorMessage += `: Cannot connect to terminal at ${config.terminal_ip}:${config.terminal_port}. Please check network connectivity and terminal configuration.`;
        } else {
          errorMessage += `: ${error.message || 'Unknown error'}`;
        }

        return throwError(() => new Error(errorMessage));
      })
    );
  }

  async storeTransactionRequest(
    freedomPayResponse: FreedomPayResponse,
    transactionId: number,
    ticketId: number,
    ticketUuid: string,
    terminalId: number,
    amount: string
  ): Promise<void> {
    try {
      await this.sqliteService.storeTransaction({
        request_id: freedomPayResponse.RequestId,
        request_guid: freedomPayResponse.RequestGuid,
        transaction_id: transactionId,
        ticket_id: ticketId,
        ticket_uuid: ticketUuid,
        terminal_id: terminalId,
        amount,
        card_number: freedomPayResponse.MaskedCardNumber || null,
        approval_code: freedomPayResponse.ApprovalCode || null,
        status: 'pending',
        retry_count: 0
      });
      console.log(
        'Offline transaction stored for replay:',
        freedomPayResponse.RequestId
      );
    } catch (error) {
      console.error('Failed to store transaction request:', error);
      // Don't throw - we don't want to fail the transaction if SQLite storage fails
    }
  }

  replayOfflineTransaction(
    requestId: string,
    terminalId: number
  ): Observable<{
    success: boolean;
    response?: FreedomPayResponse;
    error?: string;
  }> {
    return this.processReplayStatus(requestId, terminalId).pipe(
      switchMap((response) => {
        if (this.isReplayStatusSuccessful(response)) {
          // Update transaction status to success in sqlite
          return from(
            this.sqliteService.updateTransactionStatus(requestId, 'success')
          ).pipe(
            switchMap(() => [{ success: true, response }]),
            catchError((error) => {
              console.error('Failed to update transaction status:', error);
              // Still return success since the replay was successful
              return [{ success: true, response }];
            })
          );
        } else {
          const errorMessage = this.getErrorMessage(response);
          return [{ success: false, error: errorMessage, response }];
        }
      }),
      catchError((error: any) => {
        const errorMessage = error.message || 'Unknown replay error';
        return [{ success: false, error: errorMessage }];
      })
    );
  }

  testTerminalConnection(terminal_id: number): Observable<boolean> {
    try {
      const config = this.getTerminalConfig(terminal_id);
      const xmlRequest = this.buildXmlQueryDeviceStatus();
      const url = `https://${config.terminal_ip}:${config.terminal_port}/`;
      const headers = { 'Content-Type': 'application/xml' };

      return from(
        this.httpPluginService.postXml(url, xmlRequest, headers)
      ).pipe(
        switchMap((resp) => {
          if (!resp) {
            return [false];
          }

          // Connection successful if we get a 2xx response
          if (resp.status >= 200 && resp.status < 300 && resp.data) {
            const parsedResponse = this.parseXmlResponse(resp.data);
            console.log('Parsed response:', parsedResponse);
            if (this.isDeviceStatusSuccessful(parsedResponse)) {
              return [true];
            } else {
              return [false];
            }
          } else {
            return [false];
          }
        }),
        catchError((error: any) => {
          console.error('Terminal connection test failed:', error);
          return [false];
        })
      );
    } catch (error) {
      console.error('Terminal configuration error:', error);
      return of(false);
    }
  }

  isTransactionSuccessful(response: FreedomPayResponse): boolean {
    return (
      response.Decision === 'A' &&
      response.ProcessorResponseCode === '100' &&
      response.Message === 'APPROVED'
    );
  }

  isTransactionDeclined(response: FreedomPayResponse): boolean {
    return response.Decision === 'E';
  }

  isTransactionTimedOut(response: FreedomPayResponse): boolean {
    return response.Decision === 'R' && response.ErrorCode === '3137';
  }

  userCancelledTransaction(response: FreedomPayResponse): boolean {
    return response.Decision === 'R' && response.ErrorCode === '3133';
  }

  isOfflineTransactionAccepted(response: FreedomPayResponse): boolean {
    return (
      response.Decision === 'A' &&
      response.ErrorCode === '3021' &&
      response.Message === 'Approved'
    );
  }

  isOfflineTransactionDeclined(response: FreedomPayResponse): boolean {
    return (
      (response.Decision === 'R' && response.ErrorCode === '3022') ||
      (response.Decision === 'R' && response.ErrorCode === '3042') ||
      (response.Decision === 'R' && response.ErrorCode === '3022')
    );
  }

  isReplayStatusSuccessful(response: FreedomPayResponse): boolean {
    return response.Decision === 'A' && response.ErrorCode === '0';
  }

  isReplayStatusFailed(response: FreedomPayResponse): boolean {
    return response.Decision === 'R' && response.ErrorCode === '3031';
  }

  isDeviceStatusSuccessful(response: FreedomPayResponse): boolean {
    return response.Decision === 'A' && response.ErrorCode === '0';
  }

  getErrorMessage(response: FreedomPayResponse): string {
    return (
      response.Message ||
      response.ProcessorResponseCode ||
      'Payment was declined by the terminal'
    );
  }
}
